import "./ctl-toast.js";

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-gap: var(--spacing-sm, 10px);
    --ctl-z: var(--ctl-z-toast, 2500);

    position: fixed;
    inset: auto var(--spacing-md) var(--spacing-md) auto; /* bottom-right default */
    display: flex;
    flex-direction: column;
    gap: var(--ctl-gap);
    z-index: var(--ctl-z);
    pointer-events: none; /* let clicks pass through except toast */
  }

  :host([position="bottom-left"]){
    inset: auto auto var(--spacing-md) var(--spacing-md);
    align-items: flex-start;
  }
  :host([position="top-right"]){
    inset: var(--spacing-md) var(--spacing-md) auto auto;
  }
  :host([position="top-left"]){
    inset: var(--spacing-md) auto auto var(--spacing-md);
    align-items: flex-start;
  }

  .item{
    pointer-events: auto;
    transform: translateY(var(--spacing-xxs));
    opacity: var(--opacity-0);
    transition: transform .18s ease, opacity .18s ease;
  }
  .item[data-open="true"]{
    transform: translateY(var(--opacity-0));
    opacity: var(--opacity-100);
  }
</style>

<div id="stack"></div>
`;

function uid() {
  return `toast-${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
}

export class CtlToaster extends HTMLElement {
  static get observedAttributes() {
    return ["max"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._stack = this.shadowRoot.getElementById("stack");
    this._items = [];
  }

  connectedCallback() {
    if (!this.hasAttribute("max")) this.setAttribute("max", "5");
  }

  attributeChangedCallback() {}

  /**
   * Create and show a toast.
   * @param {Object} opts
   * @param {string=} opts.title
   * @param {string=} opts.message
   * @param {"brand"|"info"|"success"|"warning"|"danger"=} opts.intent
   * @param {number=} opts.duration  ms, <=0 sticky
   * @param {boolean=} opts.closable
   * @param {Array<{label:string, onClick:Function, variant?:string}>=} opts.actions
   * @returns {string} toast id
   */
  push(opts = {}) {
    const id = uid();
    const max = Number(this.getAttribute("max") || "5") || 5;

    const wrap = document.createElement("div");
    wrap.className = "item";
    wrap.dataset.id = id;

    const toast = document.createElement("ctl-toast");
    toast.setAttribute("open", "");
    toast.setAttribute("intent", opts.intent || "info");
    toast.setAttribute("duration", String(opts.duration ?? 5000));
    if (opts.closable === false) toast.removeAttribute("closable"); // default is closable; keep simple
    else toast.setAttribute("closable", "");

    if (opts.title) {
      const t = document.createElement("span");
      t.slot = "title";
      t.textContent = opts.title;
      toast.appendChild(t);
    } else {
      // If no title, keep slot empty; message still shows
    }

    if (opts.message) {
      toast.appendChild(document.createTextNode(opts.message));
    }

    if (Array.isArray(opts.actions) && opts.actions.length) {
      opts.actions.forEach((a) => {
        const btn = document.createElement("button");
        btn.slot = "actions";
        btn.type = "button";
        btn.textContent = a.label;
        // simple inline styling to avoid coupling to ctl-button;
        // you can swap to <ctl-button> later if preferred.
        btn.style.all = "unset";
        btn.style.cursor = "pointer";
        btn.style.padding = "var(--spacing-xxs) var(--spacing-sm)";
        btn.style.borderRadius = "999px";
        btn.style.border = "var(--border-width-thin) solid var(--ctl-color-border)";
        btn.style.background = "var(--ctl-color-surface)";
        btn.style.font = "600 var(--font-size-xs) var(--ctl-font-sans, system-ui)";
        btn.style.color = "var(--ctl-color-text)";
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          try { a.onClick?.(); } finally { toast.close("action"); }
        });
        toast.appendChild(btn);
      });
    }

    toast.addEventListener("ctl-toast-close", () => {
      this._remove(id);
    });

    wrap.appendChild(toast);
    this._stack.appendChild(wrap);

    // enter animation
    requestAnimationFrame(() => {
      wrap.dataset.open = "true";
    });

    this._items.push({ id, wrap, toast });

    // enforce max (drop oldest)
    while (this._items.length > max) {
      const oldest = this._items.shift();
      if (oldest) this._remove(oldest.id, { immediate: true });
    }

    return id;
  }

  dismiss(id) {
    const item = this._items.find((x) => x.id === id);
    if (item) item.toast.close("dismiss");
  }

  clear() {
    this._items.slice().forEach((x) => x.toast.close("clear"));
  }

  _remove(id, { immediate = false } = {}) {
    const idx = this._items.findIndex((x) => x.id === id);
    const item = idx >= 0 ? this._items[idx] : null;
    if (!item) return;

    this._items.splice(idx, 1);

    const { wrap } = item;
    wrap.dataset.open = "false";

    const finish = () => wrap.remove();

    if (immediate) return finish();

    // allow exit animation
    setTimeout(finish, 220);
  }
}

if (!customElements.get("ctl-toaster")) {
  customElements.define("ctl-toaster", CtlToaster);
}

/**
 * Optional global convenience:
 * window.ctlToast({ title, message, intent, duration })
 */
if (!window.ctlToast) {
  window.ctlToast = function ctlToast(opts) {
    let toaster = document.querySelector("ctl-toaster");
    if (!toaster) {
      toaster = document.createElement("ctl-toaster");
      toaster.setAttribute("position", "bottom-right");
      document.body.appendChild(toaster);
    }
    return toaster.push(opts);
  };
}
